import { supabase } from './supabase';
import {
  formatBrandGuidelinesForPrompt,
  getBrandGuidelines,
  resolveDefaultOnlineBookingUrl,
} from './brandGuidelines';

export interface SmsOption { message: string }
export interface EmailOption { subject: string; body: string }
export type CampaignAiOption = SmsOption | EmailOption;

/**
 * Ask the AI marketing agent to write 3 split-test-ready campaign options.
 * For SMS returns [{ message }], for email returns [{ subject, body }].
 * Loads brand guidelines when available and passes them to the edge function.
 */
export async function writeCampaignWithAI(params: {
  channel: 'email' | 'sms';
  prompt: string;
  businessId: string;
  businessName?: string;
  tone?: string;
  logoUrl?: string | null;
  /** Connected Nexus site — used when brand website_url is empty. */
  websiteUrlFallback?: string | null;
  businessSlug?: string | null;
}): Promise<CampaignAiOption[]> {
  if (!supabase) throw new Error('Supabase client not initialized');

  let brandGuidelines = '';
  try {
    const guidelines = await getBrandGuidelines(params.businessId);
    let onlineBookingUrlFallback: string | undefined;
    if (!(guidelines?.online_booking_url || '').trim()) {
      onlineBookingUrlFallback =
        (await resolveDefaultOnlineBookingUrl(params.businessId, params.businessSlug)) ||
        undefined;
    }
    brandGuidelines = formatBrandGuidelinesForPrompt(guidelines, {
      logoUrl: params.logoUrl,
      websiteUrlFallback: params.websiteUrlFallback,
      onlineBookingUrlFallback,
    });
  } catch (err) {
    console.warn('Could not load brand guidelines for AI campaign write:', err);
  }

  const { data, error } = await supabase.functions.invoke('ai-write-campaign', {
    body: {
      channel: params.channel,
      prompt: params.prompt,
      businessId: params.businessId,
      businessName: params.businessName,
      tone: params.tone,
      brandGuidelines: brandGuidelines || undefined,
    },
  });
  if (error) {
    let msg = error.message;
    try {
      const ctx = (error as any).context;
      if (ctx?.json) { const b = await ctx.clone().json(); if (b?.error) msg = b.error; }
    } catch { /* ignore */ }
    throw new Error(msg || 'AI request failed');
  }
  if (!data?.success) throw new Error(data?.error || 'AI request failed');
  return (data.options || []) as CampaignAiOption[];
}
